import client from "./client";
import type { PaginatedResponse } from "@/types/common";
import type {
  Employee,
  EmployeeCreateData,
  EmployeeFilters,
  EmployeeListItem,
  OnboardingReadiness,
  StartOnboardingResponse,
} from "@/types/employee";

export async function listEmployees(
  page = 1,
  perPage = 20,
  filters?: EmployeeFilters
): Promise<PaginatedResponse<EmployeeListItem>> {
  const params: Record<string, string> = {
    page: String(page),
    per_page: String(perPage),
  };
  if (filters?.search) params.search = filters.search;
  if (filters?.department_id) params.department_id = filters.department_id;
  if (filters?.status) params.status = filters.status;
  if (filters?.employment_type) params.employment_type = filters.employment_type;

  const res = await client.get<PaginatedResponse<EmployeeListItem>>("/employees", {
    params,
  });
  return res.data;
}

export async function getEmployee(id: string): Promise<Employee> {
  const res = await client.get<Employee>(`/employees/${id}`);
  return res.data;
}

export async function createEmployee(data: EmployeeCreateData): Promise<Employee> {
  const res = await client.post<Employee>("/employees", data);
  return res.data;
}

/**
 * Manually kick off the onboarding pipeline for an employee that was
 * created as ``employee_type='new_hire'`` (status=pending). Backend
 * fans out documents, notifies the manager, and dispatches Pilot;
 * returns a summary of what happened.
 *
 * Backend gates this on MANAGE_EMPLOYEES so HR_ADMIN, SUPER_ADMIN,
 * and MANAGER can all call it. 422 when the employee is "existing"
 * or already completed.
 */
export async function startOnboarding(
  employeeId: string,
  options?: { force?: boolean },
): Promise<StartOnboardingResponse> {
  // ``force=true`` is what the SPA passes when the admin clicked
  // "Send anyway" on the warning modal — the backend rejects with 409
  // by default if any applicable template has no uploaded file.
  const res = await client.post<StartOnboardingResponse>(
    `/employees/${employeeId}/start-onboarding`,
    null,
    { params: options?.force ? { force: "true" } : {} },
  );
  return res.data;
}

/**
 * Pre-flight readiness check for the Start Onboarding modal. Cheap —
 * just walks active templates and returns counts/lists. Used to show
 * the "no documents configured" or "missing file" warnings before the
 * admin commits.
 */
export async function getOnboardingReadiness(
  employeeId: string,
): Promise<OnboardingReadiness> {
  const res = await client.get<OnboardingReadiness>(
    `/employees/${employeeId}/onboarding-readiness`,
  );
  return res.data;
}

export async function updateEmployee(
  id: string,
  data: Partial<Employee>
): Promise<Employee> {
  const res = await client.put<Employee>(`/employees/${id}`, data);
  return res.data;
}

export async function getOrgChart(): Promise<
  Array<{
    id: string;
    first_name: string;
    last_name: string;
    title: string;
    avatar_url: string | null;
    department: string | null;
    manager_id: string | null;
  }>
> {
  const res = await client.get("/employees/org-chart");
  return res.data;
}


// ── Lenavio Org — nested-tree endpoint ─────────────────────────

export interface OrgChartNode {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  title: string;
  department: string | null;
  avatar_url: string | null;
  direct_reports: OrgChartNode[];
}

/**
 * Fetch the org chart as a nested tree built server-side from
 * manager_id relationships. The top-level array contains all
 * employees with no manager (typically just the CEO; can include
 * orphans during a restructure).
 */
export async function getOrgChartTree(): Promise<OrgChartNode[]> {
  const res = await client.get<OrgChartNode[]>("/org-chart");
  return res.data;
}

export async function getDirectReports(
  employeeId: string
): Promise<EmployeeListItem[]> {
  const res = await client.get<EmployeeListItem[]>(
    `/employees/${employeeId}/direct-reports`
  );
  return res.data;
}

// -------- Offboarding --------

export interface OffboardRequest {
  termination_date: string;
  reason?: string;
  notes?: string;
  reassign_reports_to?: string;
  deactivate_user?: boolean;
  /** When false, the COBRA notice email + document assignment are
   *  NOT generated. The CobraEvent row is still created so the admin
   *  can resend later. Defaults to true server-side. */
  send_cobra_notice?: boolean;
}

export async function offboardEmployee(
  id: string,
  data: OffboardRequest
): Promise<Employee> {
  const res = await client.post<Employee>(`/employees/${id}/offboard`, data);
  return res.data;
}

// -------- CSV import --------

export interface CsvImportIssue {
  row: number;
  field: string;
  message: string;
}

export interface CsvImportPreviewRow {
  row: number;
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  department: string;
  hire_date: string;
  employment_type: string;
}

/**
 * Response shape for both /employees/import (dry-run) and
 * /employees/import/confirm (real import). The two endpoints return the
 * same envelope — only ``imported`` differs (0 on dry-run).
 */
export interface CsvImportReport {
  valid_count: number;
  error_count: number;
  warnings_count: number;
  errors: CsvImportIssue[];
  warnings: CsvImportIssue[];
  preview: CsvImportPreviewRow[];
  departments_created: string[];
  imported: number;
  skipped: number;
  total_rows: number;
  dry_run: boolean;
  // Legacy field — kept so the old page still renders.
  created?: number;
}

// Kept as an alias for the old page (which still imports this name).
export type CsvImportResult = CsvImportReport & {
  errors: (CsvImportIssue & { error?: string })[];
};

export async function validateEmployeesCsv(
  file: File,
  createUserAccounts = true,
  defaultEmployeeType: "new_hire" | "existing" = "existing",
): Promise<CsvImportReport> {
  const form = new FormData();
  form.append("file", file);
  form.append("create_user_accounts", String(createUserAccounts));
  form.append("default_employee_type", defaultEmployeeType);
  const res = await client.post<CsvImportReport>("/employees/import", form);
  return res.data;
}

export async function confirmEmployeesCsv(
  file: File,
  createUserAccounts = true,
  defaultEmployeeType: "new_hire" | "existing" = "existing",
): Promise<CsvImportReport> {
  const form = new FormData();
  form.append("file", file);
  form.append("create_user_accounts", String(createUserAccounts));
  form.append("default_employee_type", defaultEmployeeType);
  const res = await client.post<CsvImportReport>(
    "/employees/import/confirm",
    form
  );
  return res.data;
}

export function employeeImportTemplateUrl(): string {
  // Full absolute URL so the browser can download directly without
  // going through the Axios interceptor stack.
  const base = (client.defaults.baseURL || "").replace(/\/+$/, "");
  return `${base}/employees/import/template`;
}

// Back-compat shim for callers that still use the old JSON endpoint.
export async function importEmployeesCsv(
  csvContent: string,
  createUserAccounts = false,
  dryRun = false
): Promise<CsvImportResult> {
  const res = await client.post<CsvImportResult>("/employees/import/csv", {
    csv_content: csvContent,
    create_user_accounts: createUserAccounts,
    dry_run: dryRun,
  });
  return res.data;
}

// -------- Avatar --------

export async function updateAvatar(
  id: string,
  avatarUrl: string
): Promise<Employee> {
  const res = await client.put<Employee>(`/employees/${id}/avatar`, {
    avatar_url: avatarUrl,
  });
  return res.data;
}

// -------- Compensation history --------

export interface CompensationEntry {
  id: string;
  employee_id: string;
  effective_date: string;
  amount: number;
  currency: string;
  period: string;
  change_type: string;
  reason: string | null;
  title_at_time: string | null;
  created_at: string;
}

export interface CompensationEntryCreate {
  effective_date: string;
  amount: number;
  currency?: string;
  period?: string;
  change_type?: string;
  reason?: string;
  title_at_time?: string;
}

export async function listCompensation(
  employeeId: string
): Promise<CompensationEntry[]> {
  const res = await client.get<CompensationEntry[]>(
    `/employees/${employeeId}/compensation`
  );
  return res.data;
}

export async function addCompensation(
  employeeId: string,
  data: CompensationEntryCreate
): Promise<CompensationEntry> {
  const res = await client.post<CompensationEntry>(
    `/employees/${employeeId}/compensation`,
    data
  );
  return res.data;
}
